import { OneTrustCategory } from '@square/onetrust-compliant-access';
import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { useLocalStorage, UseStorageSetter } from '@squareup/dex-ui-hooks';
import { Box } from '@squareup/dex-ui-shared-base';
import clsx from 'clsx';
import React, { ReactNode, useCallback, useEffect } from 'react';

import { SunIcon, MoonIcon } from '../../Icons';
import { NativeButton } from '../../NativeButton';

import styles from './theme-switcher.module.css';

const DARK_PAGE_MODE = 'dark';

/**
 * IMPORTANT! If you change this key, all customers' existing theme preferences
 * will be lost. Do not change it unless you're ok with this happening.
 *
 * This constant is referred to in the `index.html` for app as well to prevent
 * the flash of "white" before this service sets the class appropiately. If you
 * change this key, you should change it there too.
 */
const LOCAL_STORAGE_KEY = 'themeSwitcher-isDark';

/**
 * Sets the current page theme mode class to undefined or dark
 * on the html element based on the provided dark mode flag.
 */
function setPageDarkModeEnabled(html: HTMLElement, isInDarkMode: boolean) {
  if (isInDarkMode) {
    html.classList.add(DARK_PAGE_MODE);
  } else {
    html.classList.remove(DARK_PAGE_MODE);
  }
}

function persistDarkMode(
  isInDarkMode: boolean,
  setLocalStorageItem: UseStorageSetter<boolean>
) {
  const html = document.documentElement;
  setPageDarkModeEnabled(html, isInDarkMode);
  setLocalStorageItem(isInDarkMode);
}

/**
 * The switcher tracks whether or not the technical reference is in dark mode
 * and writes the theme preference to local storage.
 *
 * @note This code works in-concert with an inline-script defined in
 * `_document.tsx` that sets the `dark` class before the CSS loads so that there's
 * no flash of "white" before dark mode kicks in. If you change values such as
 * the `LOCAL_STORAGE_KEY`, make sure to change it as well.
 */
const ThemeSwitcher = ({
  children,
  className,
}: {
  children?: ReactNode;
} & NullableClassName) => {
  const [isInDarkMode, setIsInDarkMode, readFromStorage] =
    useLocalStorage<boolean>({
      key: LOCAL_STORAGE_KEY,
      initialValue: false,
      category: OneTrustCategory.FUNCTIONAL,
    });
  // Wait until document is defined then sync dark mode with local state.
  useEffect(() => {
    const html = document.documentElement;
    const isDarkModeClassOnPage = html.classList.contains(DARK_PAGE_MODE);
    if (isDarkModeClassOnPage || isInDarkMode) {
      persistDarkMode(true, setIsInDarkMode);
    }
  }, [isInDarkMode, setIsInDarkMode]);

  const toggleTheme = useCallback(() => {
    const currentIsInDarkMode = !isInDarkMode;
    persistDarkMode(currentIsInDarkMode, setIsInDarkMode);
  }, [isInDarkMode, setIsInDarkMode]);

  if (!readFromStorage) {
    // If we haven't read the value from storage, don't render the switcher yet.
    // This prevents the switcher from flickering between light and dark mode.
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <NativeButton
        testId="theme-switcher"
        trackingId="toggle-theme-switcher"
        className={clsx(styles['theme-switcher'], className)}
        onClick={toggleTheme}
        aria-label="Switch Theme"
      >
        <Box
          className={clsx(
            styles['theme-switcher__inner'],
            isInDarkMode && styles['theme-switcher--dark']
          )}
        >
          <Box className={styles['theme-switcher__indicator']}>
            <SunIcon className={styles['theme-switcher__indicator__sun']} />
            <MoonIcon className={styles['theme-switcher__indicator__moon']} />
          </Box>
        </Box>
      </NativeButton>
    </>
  );
};

export { ThemeSwitcher };
